// src/components/CookAI/common/Reveal.tsx
import React, { useEffect, useRef, useState, CSSProperties } from "react";

interface RevealProps {
    children: React.ReactNode;
    /** Delay in ms before the reveal transition starts once visible */
    delay?: number;
    /** Optional extra styles for the wrapper */
    style?: CSSProperties;
}

/**
 * Fade-up-on-scroll wrapper. Renders children invisible & shifted down,
 * then transitions them in the first time they enter the viewport.
 */
const Reveal: React.FC<RevealProps> = ({ children, delay = 0, style }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        // Fallback for very old browsers — just show content
        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(28px)",
                transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
                willChange: "opacity, transform",
                ...style,
            }}
        >
            {children}
        </div>
    );
};

export default Reveal;
